import { CollectionViewSentryContext, LongTaskMonitorDeps, LongTaskThresholdResult } from './types';

/** Single long task at or above this duration triggers a warning event. */
export const LONG_TASK_SINGLE_THRESHOLD_MS = 500;

/** Rolling window length for aggregating long-task duration. */
export const LONG_TASK_WINDOW_MS = 10_000;

/** Sum of long tasks in the window at or above this total triggers a warning event. */
export const LONG_TASK_WINDOW_TOTAL_THRESHOLD_MS = 2_000;

interface LongTaskWindowEntry {
  startMs: number;
  durationMs: number;
}

export function isLongTaskMonitoringSupported(): boolean {
  if (typeof PerformanceObserver === 'undefined') {
    return false;
  }

  return PerformanceObserver.supportedEntryTypes?.includes('longtask') ?? false;
}

export function evaluateLongTaskThreshold(
  durationMs: number,
  entries: LongTaskWindowEntry[],
  nowMs: number,
): LongTaskThresholdResult {
  const recentEntries = entries.filter(entry => nowMs - entry.startMs <= LONG_TASK_WINDOW_MS);
  const windowTotalMs = recentEntries.reduce((sum, entry) => sum + entry.durationMs, 0);

  return {
    shouldCapture:
      durationMs >= LONG_TASK_SINGLE_THRESHOLD_MS ||
      windowTotalMs >= LONG_TASK_WINDOW_TOTAL_THRESHOLD_MS,
    windowTotalMs,
    singleTaskMs: durationMs,
  };
}

export class LongTaskMonitor {
  private stopMonitoring: (() => void) | undefined;
  private readonly windowEntries: LongTaskWindowEntry[] = [];
  private lastPathname = '';
  private hasCapturedWarningThisPage = false;
  private collectionViewContext: CollectionViewSentryContext | undefined;

  constructor(private readonly deps: LongTaskMonitorDeps) {}

  start() {
    this.stop();

    if (!isLongTaskMonitoringSupported()) {
      return;
    }

    try {
      const observer = new PerformanceObserver(list => {
        for (const entry of list.getEntries()) {
          this.handleLongTask(entry);
        }
      });

      observer.observe({ type: 'longtask', buffered: true });
      this.stopMonitoring = () => observer.disconnect();
    } catch {
      this.stopMonitoring = undefined;
    }
  }

  stop() {
    this.stopMonitoring?.();
    this.stopMonitoring = undefined;
  }

  setCollectionViewContext(context: CollectionViewSentryContext) {
    this.collectionViewContext = context;
  }

  clearCollectionViewContext() {
    this.collectionViewContext = undefined;
  }

  private handleLongTask(entry: PerformanceEntry) {
    const durationMs = entry.duration;
    const startMs = entry.startTime;
    const nowMs = performance.now();
    const pathname = window.location.pathname;

    if (pathname !== this.lastPathname) {
      this.lastPathname = pathname;
      this.hasCapturedWarningThisPage = false;
    }

    this.windowEntries.push({ startMs, durationMs });

    const recentEntries = this.windowEntries.filter(
      item => nowMs - item.startMs <= LONG_TASK_WINDOW_MS,
    );
    this.windowEntries.length = 0;
    this.windowEntries.push(...recentEntries);

    const threshold = evaluateLongTaskThreshold(durationMs, this.windowEntries, nowMs);

    this.deps.breadcrumb({
      category: 'performance',
      message: 'longtask',
      data: { durationMs, startMs, pathname, windowTotalMs: threshold.windowTotalMs },
    });

    if (!threshold.shouldCapture || this.hasCapturedWarningThisPage) {
      return;
    }

    this.hasCapturedWarningThisPage = true;
    this.deps.captureLongTaskWarning({
      durationMs,
      startMs,
      pathname,
      windowTotalMs: threshold.windowTotalMs,
      collectionViewContext: this.collectionViewContext,
    });
  }
}
